/**
 * DiscoveryCallPanel — guided PEM discovery call surface
 * (Phase 5g-a / Luisa R5, Jun 11 2026).
 *
 * Implements Luisa Parnell's PEM Job Aide (docs/roles/
 * luisa_patient_education_2026-06-10.md Addendum 1, Daniel's 2026-06-11
 * authoritative-flow revision) as a live call aid:
 *
 *   - The 9 steps as collapsible script cards with Luisa's verbatim
 *     prompts and copy-ready one-liners (Step 5's "explain simply").
 *   - Structured capture inline with the flow: discovery free-text,
 *     the emotional WHY (Step 2), and the Step-3 qualification factors.
 *   - THINGS TO AVOID guardrail strip pinned at the top.
 *   - Assumptive-close reminder on the wrap-up (Step 9) + consult-
 *     scheduled toggle.
 *
 * Saves through discovery-calls:save (free text encrypted server-side)
 * and logs a Conversations timeline entry. Prior captures listed below
 * the form. PEM mission header keeps the role boundary front and center.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Phone, ChevronDown, ChevronRight, ShieldAlert, Copy, CheckCircle2,
  Loader2, AlertCircle, Heart, History, BookOpen,
} from "lucide-react";
// Card P77 item 10 — staff can finally READ the guide they keep sending.
import EducationGuideModal from "./EducationGuideModal";

const STEPS = [
  {
    n: 1, title: "Build rapport",
    script: '"Thank you for taking the time to speak with me today. Before we discuss treatment options, I\'d love to learn a little more about you and what brought you to Zignagenix."',
    coach: "Ask: what condition? how long? what have they tried? how does it affect daily life? what motivated them? Listen more than you talk.",
  },
  {
    n: 2, title: "Discover their WHY",
    script: '"If your treatment is successful, what would that allow you to do again?" … "How important would that be for you?"',
    coach: "Walk without pain · play golf · travel · grandchildren · independence. This is the emotional anchor — capture it below, every later touchpoint references it.",
  },
  {
    n: 3, title: "Qualify",
    script: '"Are you actively looking to move forward with treatment if a physician believes you\'re a good candidate?" · "How soon would you like to begin treatment?"',
    coach: "Determine: condition ✓ severity ✓ prior treatments ✓ timeframe ✓ travel readiness ✓ financial readiness.",
  },
  {
    n: 4, title: "Position Zignagenix (under 2 minutes)",
    script: '"Based on what you\'ve shared, you\'re exactly the type of patient our physicians evaluate every day."',
    coach: "Own laboratory · live, fresh products (never cryopreserved) · physician-directed plans · concierge support · transportation assistance · multiple Mexico clinics. Never overwhelm.",
  },
  {
    n: 5, title: "Explain therapies SIMPLY",
    script: '"Our physicians have several regenerative tools available, including stem cells, Natural Killer cells, Muse cells, exosomes, PRP, and peptide therapies. The specific recommendation depends on your medical history, condition, goals, and physician evaluation."',
    coach: "Then STOP talking. No lengthy scientific explanations.",
    copyable: true,
  },
  {
    n: 6, title: "Build credibility",
    script: '"Our physicians evaluate patients from around the world dealing with orthopedic conditions, autoimmune disorders, neurological conditions, chronic inflammation, and healthy aging concerns. Every patient is different, which is why physician evaluation is so important."',
  },
  {
    n: 7, title: "Set expectations",
    script: '"Our goal is to help improve quality of life and support the body\'s natural healing processes."',
    coach: "Results vary · healing takes time · some improve quickly, others gradually · NEVER promise results.",
  },
  {
    n: 8, title: "Transition to the doctor",
    script: '"The next step is a complimentary physician consultation."',
    coach: "The physician will: review history + imaging ✓ discuss goals ✓ determine candidacy ✓ recommend options ✓ answer medical questions.",
  },
  {
    n: 9, title: "Assumptive close",
    script: '"Would mornings or afternoons work better for your physician consultation?" OR "Is tomorrow or Thursday better for you?"',
    coach: 'NEVER ask "Would you like to schedule?" — always guide toward scheduling.',
  },
];

const POWER_PHRASES = [
  "I understand.", "Tell me more about that.", "How has that impacted your life?",
  "That's exactly why we schedule physician consultations.",
  "Based on what you've shared, I believe it makes sense to have one of our physicians review your case.",
  "Our goal is to help you make an informed decision.",
];

/**
 * DiscoveryCaptureForm — the Discovery Call QUESTIONS, and the only definition
 * of them.
 *
 * ⛔⛔ MOVED, NOT COPIED — Card CONTACT-PROFILE-INTAKE item 7.
 *
 * Carlos asked for the discovery questions on the Profile tab under Patient
 * intake, and for Care > Discovery to keep working on the same data. Two
 * copies of this markup would have satisfied the first sentence and quietly
 * broken the second the first time somebody added a question to one of them.
 * So the form is lifted out whole — the same fields, the same `form` state,
 * the same `discovery-calls:save` call, the same clear-and-reload — and both
 * screens render THIS.
 *
 * ⚠ The two flags below subtract; they never substitute. `showGuide` and
 * `showHistory` are TRUE for Care > Discovery, which is therefore byte-for-byte
 * the screen it was. The intake section passes false for both because the
 * education-guide send and the prior-call list belong to the call surface, not
 * to an intake form — and NEITHER flag touches a question or the save path.
 *
 * @param {object}   props
 * @param {number}   props.contactId
 * @param {boolean} [props.showGuide=true]    render the education-guide sender
 * @param {boolean} [props.showHistory=true]  render prior discovery captures
 * @param {string}  [props.heading]           column heading
 * @param {function} [props.onSaved]          called after the server confirms
 */
export function DiscoveryCaptureForm({
  contactId,
  showGuide = true,
  showHistory = true,
  heading = "Capture as you go",
  onSaved,
}) {
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [guideSent, setGuideSent] = useState("");
  // Card P77 item 10: which language the in-CRM viewer opens in, or null for
  // closed. Separate from sendGuide's language — reading the Spanish guide to
  // check a phrase should not send anything.
  const [viewGuideLang, setViewGuideLang] = useState(null);
  const [sendingGuide, setSendingGuide] = useState(false);

  // Capture form state
  const [form, setForm] = useState({
    conditionsText: "", priorTreatmentsText: "", dailyImpactText: "",
    whyMotivation: "", whyImportance: "",
    severity: "", timeframe: "", travelReady: "", financialReadiness: "",
    activelyMovingForward: "", consultScheduled: false, outcomeNote: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  const load = useCallback(async () => {
    if (!contactId) return;
    try {
      const r = await window.zignagenixAPI.invoke("discovery-calls:list", { contactId });
      if (Array.isArray(r)) setHistory(r);
    } catch (_) { /* panel degrades */ }
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true); setErr(""); setSaved(false);
    try {
      const r = await window.zignagenixAPI.invoke("discovery-calls:save", {
        contactId,
        ...form,
        travelReady: form.travelReady === "" ? null : form.travelReady === "yes",
        activelyMovingForward: form.activelyMovingForward === "" ? null : form.activelyMovingForward === "yes",
      });
      if (r && r.error) setErr(r.message || r.code);
      else {
        setSaved(true);
        setForm({
          conditionsText: "", priorTreatmentsText: "", dailyImpactText: "",
          whyMotivation: "", whyImportance: "",
          severity: "", timeframe: "", travelReady: "", financialReadiness: "",
          activelyMovingForward: "", consultScheduled: false, outcomeNote: "",
        });
        await load();
        if (onSaved) onSaved();
      }
    } catch (e) { setErr(e.message || "Save failed"); }
    setSaving(false);
  }

  async function sendGuide(lang) {
    setSendingGuide(true); setGuideSent(""); setErr("");
    try {
      const r = await window.zignagenixAPI.invoke("education-guide:send", { contactId, lang });
      if (r && r.error) setErr(r.message || r.code);
      // ⛔ Card P86 — do not say "queued via SMS + email" while the kill
      // switch is off; the dispatcher will record those rows blocked.
      else if (r && r.messagingEnabled === false)
        setGuideSent(`${lang.toUpperCase()} guide link ready. Not sent yet: texting and email go live with the Twilio integration.`);
      else setGuideSent(r.queuedChannels && r.queuedChannels.length
        ? `${lang.toUpperCase()} guide queued via ${r.queuedChannels.join(" + ")}.`
        : `${lang.toUpperCase()} guide link ready (no SMS/email on file).`);
    } catch (e) { setErr(e.message || "Send failed"); }
    setSendingGuide(false);
  }

  return (
    <div style={{ border: "1px solid #e1e6ed", borderRadius: 8, background: "#fff", padding: 12, alignSelf: "start" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#212529", marginBottom: 8 }}>
        {heading}
      </div>

        <label style={lbl}>Condition(s) + how long (Step 1)</label>
        <textarea rows={2} value={form.conditionsText} onChange={set("conditionsText")} style={inp} />

        <label style={lbl}>Treatments already tried</label>
        <textarea rows={2} value={form.priorTreatmentsText} onChange={set("priorTreatmentsText")} style={inp} />

        <label style={lbl}>Impact on daily life</label>
        <textarea rows={2} value={form.dailyImpactText} onChange={set("dailyImpactText")} style={inp} />

        <div style={{ padding: "8px 10px", background: "#FDF4FF", border: "1px solid #F0ABFC", borderRadius: 8, margin: "8px 0" }}>
          <label style={{ ...lbl, color: "#86198F", display: "flex", alignItems: "center", gap: 4, marginTop: 0 }}>
            <Heart size={11} /> Their WHY (Step 2) — the emotional anchor
          </label>
          <textarea rows={2} placeholder='"What would success let them do again?"' value={form.whyMotivation} onChange={set("whyMotivation")} style={inp} />
          <label style={{ ...lbl, color: "#86198F" }}>How important is that to them?</label>
          <input type="text" value={form.whyImportance} onChange={set("whyImportance")} style={inp} />
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.04em", margin: "8px 0 2px" }}>
          Qualification (Step 3)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <label style={lbl}>Severity</label>
            <select value={form.severity} onChange={set("severity")} style={inp}>
              <option value="">—</option>
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Timeframe to begin</label>
            <select value={form.timeframe} onChange={set("timeframe")} style={inp}>
              <option value="">—</option>
              <option value="immediate">Immediately</option>
              <option value="one_to_three_months">1–3 months</option>
              <option value="three_to_six_months">3–6 months</option>
              <option value="exploring">Just exploring</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Willing to travel</label>
            <select value={form.travelReady} onChange={set("travelReady")} style={inp}>
              <option value="">—</option>
              <option value="yes">Yes</option>
              <option value="no">No / unsure</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Financial readiness</label>
            <select value={form.financialReadiness} onChange={set("financialReadiness")} style={inp}>
              <option value="">—</option>
              <option value="cash_ready">Cash ready</option>
              <option value="financing_needed">Financing needed</option>
              <option value="shopping_around">Shopping around</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
        </div>
        <label style={lbl}>Actively moving forward if candidacy confirmed?</label>
        <select value={form.activelyMovingForward} onChange={set("activelyMovingForward")} style={inp}>
          <option value="">—</option>
          <option value="yes">Yes</option>
          <option value="no">Not yet</option>
        </select>

        <label style={lbl}>Wrap-up note (Step 9)</label>
        <textarea rows={2} value={form.outcomeNote} onChange={set("outcomeNote")} style={inp} />
        <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#15803D", margin: "6px 0 10px", cursor: "pointer" }}>
          <input type="checkbox" checked={form.consultScheduled} onChange={set("consultScheduled")} />
          Physician consultation scheduled (assumptive close landed)
        </label>

        {showGuide && (<>
        {/* R5 handoff: send the education guide after the call (Step 8). */}
        <div style={{ borderTop: "1px solid #f0f3f7", margin: "10px 0", paddingTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 5, display: "flex", alignItems: "center", gap: 4 }}>
            <BookOpen size={11} /> Send education guide
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => sendGuide("en")} disabled={sendingGuide} style={{ ...guideBtn, flex: 1 }}>
              {sendingGuide ? <Loader2 size={11} className="spin" /> : "English"}
            </button>
            <button onClick={() => sendGuide("es")} disabled={sendingGuide} style={{ ...guideBtn, flex: 1 }}>
              {sendingGuide ? <Loader2 size={11} className="spin" /> : "Español"}
            </button>
          </div>
          {guideSent && <div style={{ fontSize: 11, color: "#15803D", marginTop: 5 }}>{guideSent}</div>}
          {/* ⛔ VIEW IS NOT SEND — Card P77 item 10 (#619). Until that card the
              only thing anyone could do with the guide was mail it to a patient
              unread; there was no surface in the CRM that showed it, and
              /education replaces the whole app shell. This opens it over the
              CRM instead.

              ⚠ MERGE NOTE (#618 ← #619): this button lived in the inline
              capture column that #618 extracted into DiscoveryCaptureForm, so
              the merge dropped the button and left its modal in the OUTER panel
              where `viewGuideLang` is not in scope — a ReferenceError the first
              time anyone pressed it. Both halves are reunited here, inside
              `showGuide`: Care > Discovery keeps it, and the Patient intake
              section (showGuide={false}) does not, which is right — reading the
              guide belongs to the call surface. */}
          <button
            type="button"
            data-testid="view-education-guide"
            onClick={() => setViewGuideLang("en")}
            style={{ ...guideBtn, width: "100%", marginTop: 6 }}
            title="Read the guide and save it as a PDF — nothing is sent"
          >
            <BookOpen size={11} /> View / download guide
          </button>
          {viewGuideLang && (
            <EducationGuideModal
              initialLang={viewGuideLang}
              onClose={() => setViewGuideLang(null)}
            />
          )}
        </div>

        </>)}

        {err && <div style={errBox}><AlertCircle size={13} /> {err}</div>}
        {saved && <div style={okBox}><CheckCircle2 size={13} /> Discovery saved + logged to Conversations.</div>}

        <button onClick={save} disabled={saving} style={{
          width: "100%", padding: "8px 12px", background: "#2E8B98", color: "#fff",
          border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700,
          cursor: saving ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          {saving ? <><Loader2 size={13} className="spin" /> Saving…</> : "Save discovery capture"}
        </button>

        {/* History */}
        {showHistory && history.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
              <History size={11} /> Previous discovery calls
            </div>
            {history.map((h) => (
              <div key={h.id} style={{ fontSize: 11.5, color: "#374151", padding: "7px 9px", border: "1px solid #f0f3f7", borderRadius: 6, marginBottom: 4, background: "#FAFBFC" }}>
                <div style={{ color: "#6C757D", marginBottom: 2 }}>
                  {new Date(h.created_at).toLocaleString()}{h.captured_by_name ? ` · ${h.captured_by_name}` : ""}
                  {h.consult_scheduled && <span style={{ color: "#15803D", fontWeight: 700 }}> · consult scheduled</span>}
                </div>
                {h.why_motivation && <div><strong>WHY:</strong> {h.why_motivation}</div>}
                {h.conditions_text && <div><strong>Condition:</strong> {h.conditions_text}</div>}
                {(h.severity || h.timeframe || h.financial_readiness) && (
                  <div style={{ color: "#6C757D" }}>
                    {[h.severity, h.timeframe?.replace(/_/g, " "), h.financial_readiness?.replace(/_/g, " ")].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

export default function DiscoveryCallPanel({ contactId, user }) {
  const [openStep, setOpenStep] = useState(1);
  const [copied, setCopied] = useState(false);

  const copySimpleExplainer = () => {
    navigator.clipboard?.writeText(STEPS[4].script.replace(/^"|"$/g, ""));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      {/* ── Mission header ─────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <Phone size={16} color="#2E8B98" />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#212529" }}>Guided discovery call</div>
          <div style={{ fontSize: 11, color: "#6C757D" }}>
            PEM = Build Trust → Discover Needs → Schedule Physician Consultation. Your job is NOT to sell stem cells.
          </div>
        </div>
      </div>

      {/* ── Guardrail strip ─────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        padding: "7px 12px", marginBottom: 12, borderRadius: 8,
        background: "#FEF2F2", border: "1px solid #FECACA", fontSize: 11, color: "#991B1B",
      }}>
        <ShieldAlert size={13} />
        <strong>Avoid:</strong>
        {["diagnosing", "medical claims", "guaranteeing results", "early pricing", "over-explaining", "talking more than the patient", "rushing to recommendations"].map((t) => (
          <span key={t} style={{ background: "#fff", border: "1px solid #FECACA", borderRadius: 999, padding: "1px 8px" }}>✘ {t}</span>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)", gap: 14 }}>
        {/* ── Left: the 9-step script ─────────────────────────────── */}
        <div>
          {STEPS.map((s) => {
            const open = openStep === s.n;
            return (
              <div key={s.n} style={{ border: "1px solid #e1e6ed", borderRadius: 8, marginBottom: 6, background: "#fff" }}>
                <button
                  onClick={() => setOpenStep(open ? null : s.n)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 8,
                    padding: "9px 12px", background: "none", border: "none",
                    cursor: "pointer", textAlign: "left",
                  }}
                >
                  {open ? <ChevronDown size={13} color="#2E8B98" /> : <ChevronRight size={13} color="#9aa4b2" />}
                  <span style={{
                    width: 20, height: 20, borderRadius: 999, background: open ? "#2E8B98" : "#E2E8F0",
                    color: open ? "#fff" : "#64748B", fontSize: 11, fontWeight: 700,
                    display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>{s.n}</span>
                  <span style={{ fontSize: 13, fontWeight: open ? 700 : 600, color: open ? "#2E8B98" : "#374151" }}>
                    {s.title}
                  </span>
                </button>
                {open && (
                  <div style={{ padding: "0 12px 12px 40px" }}>
                    <div style={{
                      fontSize: 12.5, color: "#1F2937", fontStyle: "italic", lineHeight: 1.55,
                      padding: "8px 10px", background: "#F0FDFA", borderLeft: "3px solid #2E8B98", borderRadius: 6,
                    }}>
                      {s.script}
                      {s.copyable && (
                        <button onClick={copySimpleExplainer} style={{
                          marginLeft: 8, padding: "2px 8px", fontSize: 10, fontWeight: 700,
                          background: "#fff", border: "1px solid #2E8B98", color: "#2E8B98",
                          borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3,
                        }}>
                          {copied ? <CheckCircle2 size={10} /> : <Copy size={10} />} {copied ? "Copied" : "Copy"}
                        </button>
                      )}
                    </div>
                    {s.coach && (
                      <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 6, lineHeight: 1.5 }}>{s.coach}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Power phrases */}
          <div style={{ marginTop: 8, padding: "8px 12px", background: "#F8FAFC", border: "1px solid #e1e6ed", borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 5 }}>
              Power phrases
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {POWER_PHRASES.map((p) => (
                <span key={p} style={{ fontSize: 11, color: "#374151", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 999, padding: "2px 9px" }}>
                  "{p}"
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right: structured capture — THE SHARED FORM ──────────
            ⛔ The questions used to be written out here. They now live in
            DiscoveryCaptureForm above, so the Patient intake section on the
            Profile tab renders the SAME fields through the SAME save. Both
            flags are true, so this screen is unchanged. */}
        <DiscoveryCaptureForm contactId={contactId} showGuide showHistory />
      </div>
    </div>
  );
}

const lbl = {
  display: "block", fontSize: 11, fontWeight: 600, color: "#3a4a5e", margin: "7px 0 2px",
};
const inp = {
  width: "100%", padding: "6px 8px", fontSize: 12, color: "#1f2a3b",
  border: "1px solid #c9d6e8", borderRadius: 5, background: "#fff",
  boxSizing: "border-box", resize: "vertical", fontFamily: "inherit",
};
const guideBtn = {
  padding: "6px 10px", fontSize: 12, fontWeight: 600,
  background: "#fff", color: "#2E8B98", border: "1px solid #2E8B98",
  borderRadius: 6, cursor: "pointer", display: "inline-flex",
  alignItems: "center", justifyContent: "center", gap: 4,
};
const errBox = {
  display: "flex", alignItems: "center", gap: 6, margin: "6px 0",
  padding: "6px 10px", borderRadius: 5, fontSize: 12,
  background: "#fde8e8", border: "1px solid #f5a5a5", color: "#8b0000",
};
const okBox = {
  display: "flex", alignItems: "center", gap: 6, margin: "6px 0",
  padding: "6px 10px", borderRadius: 5, fontSize: 12,
  background: "#e9f5e9", border: "1px solid #a5d5a5", color: "#2d6b2d",
};
